import {
  addPlaybookEvaluationGroupEvaluation,
  createPlaybook,
  createPlaybookEvaluation,
  createPlaybookEvaluationGroup,
  createPlaybookFromTemplate,
  createPlaybookGroupTrigger,
  createPlaybookOption,
  createPlaybookTrigger,
  createPlaybookTriggerAction,
  deletePlaybook,
  deletePlaybookAction,
  deletePlaybookEvaluation,
  deletePlaybookEvaluationGroup,
  deletePlaybookGroupTriggers,
  deletePlaybookOption,
  deletePlaybookRun,
  deletePlaybookTrigger,
  exportPlaybookRunEvaluations,
  exportPlaybookRunSummary,
  getPlaybook,
  getPlaybookRun,
  listPlaybookActions,
  listPlaybookEvaluationGroups,
  listPlaybookEvaluations,
  listPlaybookGroupTriggers,
  listPlaybookTemplates,
  removePlaybookEvaluationGroupEvaluation,
  searchPlaybook,
  searchPlaybookRuns,
  startPlaybookRun,
  updatePlaybook,
  updatePlaybookAction,
  updatePlaybookEvaluation,
  updatePlaybookEvaluationGroup,
  updatePlaybookGroupTrigger,
  updatePlaybookOption,
  updatePlaybookTrigger,
  type CreatePlaybookOptionResponse,
  type CreatePlaybookTriggerActionResponse,
  type CreatePlaybookTriggerResponse,
  type ListPlaybookActionsResponse,
  type ListPlaybookEvaluationsResponse,
  type PlaybookActionCreateRequest,
  type PlaybookActionUpdate,
  type GroupTriggerCreateRequest,
  type GroupTriggerInDb,
  type GroupTriggerUpdateRequest,
  type PlaybookEvaluationCreateRequest,
  type PlaybookEvaluationGroupCreateRequest,
  type PlaybookEvaluationGroupInDb,
  type PlaybookEvaluationGroupUpdateRequest,
  type PlaybookEvaluationUpdate,
  type PlaybookCreateRequest,
  type PlaybookInDb,
  type PlaybookOptionCreateRequest,
  type PlaybookOptionUpdateRequest,
  type PlaybookRunCreateRequest,
  PlaybookRunEvaluationExportFormat,
  type PlaybookRunInDb,
  type PlaybookRunSearchQuery,
  type PlaybookRunSearchResult,
  type PlaybookRunSummary,
  PlaybookRunSummaryExportFormat,
  type PlaybookSearchQuery,
  type PlaybookSearchResult,
  type PlaybookTemplateSummary,
  type PlaybookTriggerCreateRequest,
  type PlaybookTriggerUpdate,
  type PlaybookUpdate,
  type UpdatePlaybookActionResponse,
  type UpdatePlaybookOptionResponse,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

export interface PlaybookListQuery {
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  columnFilters?: Record<string, string[]>;
}

export interface PlaybookRunListQuery extends PlaybookListQuery {
  inputType?: PlaybookRunSearchQuery['input_type'];
}

export const PlaybooksService = {
  async search(query: PlaybookListQuery = {}): Promise<PlaybookSearchResult> {
    try {
      const pageSize = query.pageSize ?? 50;
      const page = query.page ?? 1;
      const body: PlaybookSearchQuery = {
        text: query.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: query.sortKey ?? 'created_at',
        sort_direction: query.sortDirection ?? 'desc',
        ...(query.columnFilters ? { column_filters: query.columnFilters } : {}),
      };

      const response = await searchPlaybook({ body });
      if (!response.data) {
        throw new Error('Failed to search playbooks');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search playbooks.'));
    }
  },

  async getById(uid: string): Promise<PlaybookInDb> {
    try {
      const response = await getPlaybook({ path: { playbook_uid: uid } });
      if (!response.data) {
        throw new Error('Failed to fetch playbook');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch playbook.'));
    }
  },

  async create(payload: PlaybookCreateRequest): Promise<PlaybookInDb> {
    try {
      const response = await createPlaybook({ body: payload });
      if (!response.data) {
        throw new Error('Failed to create playbook');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create playbook.'));
    }
  },

  async createFromTemplate(templateKey: string): Promise<PlaybookInDb> {
    try {
      const response = await createPlaybookFromTemplate({ path: { template_key: templateKey } });
      if (!response.data) {
        throw new Error('Failed to create playbook from template');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create playbook from template.'));
    }
  },

  async listTemplates(): Promise<PlaybookTemplateSummary[]> {
    try {
      const response = await listPlaybookTemplates();
      if (!response.data) {
        throw new Error('Failed to list playbook templates');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to list playbook templates.'));
    }
  },

  async update(uid: string, payload: PlaybookUpdate): Promise<PlaybookInDb> {
    try {
      const response = await updatePlaybook({ path: { playbook_uid: uid }, body: payload });
      if (!response.data) {
        throw new Error('Failed to update playbook');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update playbook.'));
    }
  },

  async delete(uid: string): Promise<void> {
    try {
      const { error } = await deletePlaybook({ path: { playbook_uid: uid } });
      if (error) {
        throw new Error('Failed to delete playbook');
      }
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete playbook.'));
    }
  },

  async searchRuns(
    playbookUid: string,
    query: PlaybookRunListQuery = {}
  ): Promise<PlaybookRunSearchResult> {
    try {
      const pageSize = query.pageSize ?? 50;
      const page = query.page ?? 1;
      const body: PlaybookRunSearchQuery = {
        text: query.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: query.sortKey ?? 'created_at',
        sort_direction: query.sortDirection ?? 'desc',
        ...(query.inputType ? { input_type: query.inputType } : {}),
      };

      const response = await searchPlaybookRuns({
        body,
        query: { playbook_uid: playbookUid },
      });
      if (!response.data) {
        throw new Error('Failed to search playbook runs');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search playbook runs.'));
    }
  },

  async getRun(runUid: string): Promise<PlaybookRunInDb> {
    try {
      const response = await getPlaybookRun({ path: { run_uid: runUid } });
      if (!response.data) {
        throw new Error('Failed to fetch playbook run');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch playbook run.'));
    }
  },

  async exportRunSummary(runUid: string, format: PlaybookRunSummaryExportFormat): Promise<Blob> {
    try {
      const response = await exportPlaybookRunSummary({
        path: { run_uid: runUid },
        query: { export_format: format },
        parseAs: 'blob',
      });
      const data = 'data' in response ? response.data : response;
      if (!(data instanceof Blob)) {
        throw new TypeError('Failed to export playbook run summary');
      }
      return data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to export playbook run summary.'));
    }
  },

  async exportRunEvaluations(
    runUid: string,
    format: PlaybookRunEvaluationExportFormat,
    evaluationUids?: string[]
  ): Promise<Blob> {
    try {
      const response = await exportPlaybookRunEvaluations({
        path: { run_uid: runUid },
        body: {
          export_format: format,
          evaluation_uids: evaluationUids?.length ? evaluationUids : null,
        },
        parseAs: 'blob',
      });
      const data = 'data' in response ? response.data : response;
      if (!(data instanceof Blob)) {
        throw new TypeError('Failed to export playbook run evaluations');
      }
      return data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to export playbook run evaluations.'));
    }
  },

  async startRun(
    playbookUid: string,
    payload: PlaybookRunCreateRequest
  ): Promise<PlaybookRunSummary> {
    try {
      const response = await startPlaybookRun({
        path: { playbook_uid: playbookUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to start playbook run');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to start playbook run.'));
    }
  },

  async deleteRun(runUid: string): Promise<void> {
    try {
      const { error } = await deletePlaybookRun({ path: { run_uid: runUid } });
      if (error) {
        throw new Error('Failed to delete playbook run');
      }
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete playbook run.'));
    }
  },

  async listEvaluations(uid: string): Promise<ListPlaybookEvaluationsResponse> {
    try {
      const response = await listPlaybookEvaluations({ path: { playbook_uid: uid } });
      if (!response.data) {
        throw new Error('Failed to fetch playbook evaluations');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch playbook evaluations.'));
    }
  },

  async listActions(uid: string): Promise<ListPlaybookActionsResponse> {
    try {
      const response = await listPlaybookActions({
        path: { playbook_uid: uid },
        query: { deduplicate: true },
      });
      if (!response.data) {
        throw new Error('Failed to fetch playbook actions');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch playbook actions.'));
    }
  },

  async createEvaluation(
    playbookUid: string,
    payload: PlaybookEvaluationCreateRequest
  ): Promise<ListPlaybookEvaluationsResponse[number]> {
    try {
      const response = await createPlaybookEvaluation({
        path: { playbook_uid: playbookUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create playbook evaluation');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create playbook evaluation.'));
    }
  },

  async deleteEvaluation(evaluationUid: string): Promise<void> {
    try {
      const response = await deletePlaybookEvaluation({ path: { evaluation_uid: evaluationUid } });
      if (response.error) {
        throw new Error('Failed to delete playbook evaluation');
      }
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete playbook evaluation.'));
    }
  },

  async updateEvaluation(
    evaluationUid: string,
    payload: PlaybookEvaluationUpdate
  ): Promise<ListPlaybookEvaluationsResponse[number]> {
    try {
      const response = await updatePlaybookEvaluation({
        path: { evaluation_uid: evaluationUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update playbook evaluation');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update playbook evaluation.'));
    }
  },

  async listEvaluationGroups(playbookUid: string): Promise<PlaybookEvaluationGroupInDb[]> {
    try {
      const response = await listPlaybookEvaluationGroups({
        path: { playbook_uid: playbookUid },
      });
      if (!response.data) {
        throw new Error('Failed to fetch playbook evaluation groups');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch playbook evaluation groups.'));
    }
  },

  async createEvaluationGroup(
    playbookUid: string,
    payload: PlaybookEvaluationGroupCreateRequest
  ): Promise<PlaybookEvaluationGroupInDb> {
    try {
      const response = await createPlaybookEvaluationGroup({
        path: { playbook_uid: playbookUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create playbook evaluation group');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create playbook evaluation group.'));
    }
  },

  async addEvaluationGroupEvaluation(
    groupUid: string,
    evaluationUid: string
  ): Promise<PlaybookEvaluationGroupInDb> {
    try {
      const response = await addPlaybookEvaluationGroupEvaluation({
        path: { group_uid: groupUid, evaluation_uid: evaluationUid },
      });
      if (!response.data) {
        throw new Error('Failed to add evaluation to group');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to add evaluation to group.'));
    }
  },

  async removeEvaluationGroupEvaluation(
    groupUid: string,
    evaluationUid: string
  ): Promise<PlaybookEvaluationGroupInDb> {
    try {
      const response = await removePlaybookEvaluationGroupEvaluation({
        path: { group_uid: groupUid, evaluation_uid: evaluationUid },
      });
      if (!response.data) {
        throw new Error('Failed to remove evaluation from group');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to remove evaluation from group.'));
    }
  },

  async updateEvaluationGroup(
    groupUid: string,
    payload: PlaybookEvaluationGroupUpdateRequest
  ): Promise<PlaybookEvaluationGroupInDb> {
    try {
      const response = await updatePlaybookEvaluationGroup({
        path: { group_uid: groupUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update evaluation group');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update evaluation group.'));
    }
  },

  async deleteEvaluationGroup(groupUid: string): Promise<void> {
    try {
      const { error } = await deletePlaybookEvaluationGroup({ path: { group_uid: groupUid } });
      if (error) {
        throw new Error('Failed to delete evaluation group');
      }
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete evaluation group.'));
    }
  },

  async createGroupTrigger(
    groupUid: string,
    payload: GroupTriggerCreateRequest
  ): Promise<GroupTriggerInDb> {
    try {
      const response = await createPlaybookGroupTrigger({
        path: { group_uid: groupUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create group trigger');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create group trigger.'));
    }
  },

  async listGroupTriggers(groupUid: string): Promise<GroupTriggerInDb[]> {
    try {
      const response = await listPlaybookGroupTriggers({ path: { group_uid: groupUid } });
      if (!response.data) {
        throw new Error('Failed to fetch group triggers');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch group triggers.'));
    }
  },

  async updateGroupTrigger(
    triggerUid: string,
    payload: GroupTriggerUpdateRequest
  ): Promise<GroupTriggerInDb> {
    try {
      const response = await updatePlaybookGroupTrigger({
        path: { trigger_uid: triggerUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update group trigger');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update group trigger.'));
    }
  },

  async deleteGroupTriggers(groupUid: string): Promise<void> {
    try {
      const { error } = await deletePlaybookGroupTriggers({ path: { group_uid: groupUid } });
      if (error) {
        throw new Error('Failed to delete group triggers');
      }
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete group triggers.'));
    }
  },

  async createOption(
    evaluationUid: string,
    payload: PlaybookOptionCreateRequest
  ): Promise<CreatePlaybookOptionResponse> {
    try {
      const response = await createPlaybookOption({
        path: { evaluation_uid: evaluationUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create playbook option');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create playbook option.'));
    }
  },

  async updateOption(
    optionUid: string,
    payload: PlaybookOptionUpdateRequest
  ): Promise<UpdatePlaybookOptionResponse> {
    try {
      const response = await updatePlaybookOption({
        path: { option_uid: optionUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update playbook option');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update playbook option.'));
    }
  },

  async deleteOption(optionUid: string): Promise<void> {
    try {
      await deletePlaybookOption({ path: { option_uid: optionUid } });
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete playbook option.'));
    }
  },

  async createAction(
    triggerUid: string,
    payload: PlaybookActionCreateRequest
  ): Promise<CreatePlaybookTriggerActionResponse> {
    try {
      const response = await createPlaybookTriggerAction({
        path: { trigger_uid: triggerUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create playbook action');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create playbook action.'));
    }
  },

  async updateAction(
    actionUid: string,
    payload: PlaybookActionUpdate
  ): Promise<UpdatePlaybookActionResponse> {
    try {
      const response = await updatePlaybookAction({
        path: { action_uid: actionUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to update playbook action');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update playbook action.'));
    }
  },

  async deleteAction(actionUid: string): Promise<void> {
    try {
      await deletePlaybookAction({ path: { action_uid: actionUid } });
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete playbook action.'));
    }
  },

  async createTrigger(
    evaluationUid: string,
    payload: PlaybookTriggerCreateRequest
  ): Promise<CreatePlaybookTriggerResponse> {
    try {
      const response = await createPlaybookTrigger({
        path: { evaluation_uid: evaluationUid },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create finding trigger');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create finding trigger.'));
    }
  },

  async updateTrigger(triggerUid: string, payload: PlaybookTriggerUpdate): Promise<void> {
    try {
      await updatePlaybookTrigger({ path: { trigger_uid: triggerUid }, body: payload });
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update finding trigger.'));
    }
  },

  async deleteTrigger(triggerUid: string): Promise<void> {
    try {
      await deletePlaybookTrigger({ path: { trigger_uid: triggerUid } });
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete finding trigger.'));
    }
  },
};
